import {
  normalizeFreightStatusDescribe,
  translatorForTheStatusOfStoppingPoints,
} from "../../view/frete-copy/action/action";

/**
 * Os três relatórios são três agrupamentos das mesmas linhas que o backend mede — por isso agrupar
 * é código puro aqui, e não uma segunda varredura no banco.
 *
 * Somar do lado da tela só é correto porque a soma é associativa e as páginas não se sobrepõem: o
 * cursor não repete nem pula linha. O que não é associativo é a completude — daí o teto de páginas
 * ser escrito no relatório em vez de ficar implícito.
 */

const MINUTOS = "minutos";
const NUMERO = "numero";
const TEXTO = "texto";

export const COLUNAS_DA_FROTA = [
  { chave: "numberSerial", rotulo: "Frete", tipo: TEXTO },
  { chave: "motorista", rotulo: "Motorista", tipo: TEXTO },
  { chave: "placa", rotulo: "Placa", tipo: TEXTO },
  { chave: "origem", rotulo: "Origem", tipo: TEXTO },
  { chave: "destino", rotulo: "Destino", tipo: TEXTO },
  { chave: "dataColeta", rotulo: "Coleta", tipo: TEXTO },
  { chave: "statusAtual", rotulo: "Status", tipo: TEXTO },
];

export const COLUNAS_DO_MENSAL = [
  { chave: "mesReferencia", rotulo: "Mês", tipo: TEXTO },
  { chave: "motorista", rotulo: "Motorista", tipo: TEXTO },
  { chave: "placa", rotulo: "Placa", tipo: TEXTO },
  { chave: "fretes", rotulo: "Fretes", tipo: NUMERO },
  { chave: "medidos", rotulo: "Com histórico", tipo: NUMERO },
  { chave: "movimentoMin", rotulo: "Movimento (min)", tipo: MINUTOS },
  { chave: "paradoMin", rotulo: "Parado (min)", tipo: MINUTOS },
  { chave: "operacionalMin", rotulo: "Operacional (min)", tipo: MINUTOS },
  { chave: "minutosSuspeitos", rotulo: "Fora do cálculo (min)", tipo: MINUTOS },
];

export const COLUNAS_DA_LINHA_DO_TEMPO = [
  { chave: "frete", rotulo: "Frete", tipo: TEXTO },
  { chave: "ordem", rotulo: "Ordem", tipo: NUMERO },
  { chave: "ponto", rotulo: "Ponto", tipo: TEXTO },
  { chave: "status", rotulo: "Status", tipo: TEXTO },
  { chave: "inicio", rotulo: "Início", tipo: TEXTO },
  { chave: "fim", rotulo: "Fim", tipo: TEXTO },
  { chave: "minutos", rotulo: "Duração (min)", tipo: MINUTOS },
  { chave: "localizacao", rotulo: "Localização", tipo: TEXTO },
  { chave: "observacao", rotulo: "Observação", tipo: TEXTO },
];

export function rotuloDoStatus(status) {
  const traduzido = translatorForTheStatusOfStoppingPoints(
    String(status || "")
      .trim()
      .toUpperCase(),
  );
  return Array.isArray(traduzido) ? String(status || "—") : traduzido;
}

/**
 * O status do selo da lista de fretes, decidido na mesma ordem. A regra mora dentro do componente
 * da lista (`frete-list-copy/components/data-frete-table-action.js`) e não é exportada, então é
 * cópia: mudou lá, muda aqui.
 *
 * Frete encerrado mostra o status do frete, ainda que tenha etapa velha gravada. Em aberto, vale a
 * etapa da parada em andamento, que o backend lê dos pontos de parada; sem etapa, o status do frete.
 */
export function statusAtualDoFrete(linha) {
  const doFrete = normalizeFreightStatusDescribe(linha?.status);
  const encerrado =
    ["06", "07", "08"].includes(String(linha?.statusCode ?? "").trim()) ||
    /^(entregue|finalizado|cancelado)$/i.test(doFrete);

  if (!encerrado) {
    const etapa = translatorForTheStatusOfStoppingPoints(
      String(linha?.activeStatus ?? "")
        .trim()
        .toUpperCase(),
    );
    if (typeof etapa === "string" && etapa) return etapa;
  }

  // Sem acento é como o app grava o código 05, e a lista escreve com.
  if (doFrete === "Em transito") return "Em trânsito";
  return doFrete || null;
}

export function formatarDuracao(minutos) {
  if (minutos === null || minutos === undefined) return "—";
  const total = Number(minutos);
  if (!Number.isFinite(total)) return "—";
  if (total < 60) return `${total}min`;
  return `${Math.floor(total / 60)}h${String(total % 60).padStart(2, "0")}`;
}

function dataHora(ms) {
  return ms ? new Date(ms).toLocaleString("pt-BR") : "—";
}

/** Acrescenta às linhas do backend o que só a tela sabe escrever: o status no texto da lista. */
export function prepararLinhas(linhas) {
  return (linhas || []).map((linha) => ({
    ...linha,
    statusAtual: statusAtualDoFrete(linha),
  }));
}

export function consolidarFrota(linhas) {
  const total = linhas.reduce(
    (soma, linha) => ({
      movimentoMin: soma.movimentoMin + (linha.movimentoMin || 0),
      paradoMin: soma.paradoMin + (linha.paradoMin || 0),
      operacionalMin: soma.operacionalMin + (linha.operacionalMin || 0),
      minutosSuspeitos: soma.minutosSuspeitos + (linha.minutosSuspeitos || 0),
      buracos: soma.buracos + (linha.buracos || 0),
    }),
    {
      movimentoMin: 0,
      paradoMin: 0,
      operacionalMin: 0,
      minutosSuspeitos: 0,
      buracos: 0,
    },
  );

  return {
    ...total,
    fretes: linhas.length,
    medidos: linhas.filter((l) => l.geracao === "medido").length,
    semRegistro: linhas.filter((l) => l.geracao === "sem-registro").length,
  };
}

/**
 * Mês, motorista e placa. Frete sem histórico entra na contagem de `fretes` mas não em `medidos` —
 * sem essa distinção uma média mensal dividiria por fretes que não foram medidos.
 */
export function agruparMensal(linhas) {
  const grupos = new Map();

  linhas.forEach((linha) => {
    const chave = [
      linha.mesReferencia || "—",
      linha.motorista || "—",
      linha.placa || "—",
    ].join("|");

    const atual = grupos.get(chave) || {
      mesReferencia: linha.mesReferencia || "—",
      motorista: linha.motorista || "—",
      placa: linha.placa || "—",
      fretes: 0,
      medidos: 0,
      movimentoMin: 0,
      paradoMin: 0,
      operacionalMin: 0,
      minutosSuspeitos: 0,
    };

    atual.fretes += 1;
    if (linha.geracao === "medido") atual.medidos += 1;
    atual.movimentoMin += linha.movimentoMin || 0;
    atual.paradoMin += linha.paradoMin || 0;
    atual.operacionalMin += linha.operacionalMin || 0;
    atual.minutosSuspeitos += linha.minutosSuspeitos || 0;

    grupos.set(chave, atual);
  });

  // Grupo em que nenhum frete foi medido devolve tempo nulo pelo mesmo motivo da linha do frete:
  // somar nada dá zero, e zero na tela afirma que ninguém esperou.
  grupos.forEach((grupo) => {
    if (grupo.medidos) return;
    grupo.movimentoMin = null;
    grupo.paradoMin = null;
    grupo.operacionalMin = null;
    grupo.minutosSuspeitos = null;
  });

  return [...grupos.values()].sort((a, b) => {
    if (a.mesReferencia !== b.mesReferencia) {
      return b.mesReferencia.localeCompare(a.mesReferencia);
    }
    return a.motorista.localeCompare(b.motorista);
  });
}

/**
 * As etapas de todos os fretes da varredura, numa lista só, do mais recente para o mais antigo.
 *
 * Os fretes já chegam nessa ordem — a varredura ordena por data de coleta decrescente. Aqui inverte
 * a sequência dentro de cada frete, que o backend precisa ler crescente para encadear as durações.
 *
 * A inversão é só de exibição, e por isso o `ordem` é calculado antes: ele numera o passo na
 * sequência real do frete, então a coluna desce de 6 para 1. Renumerar depois de inverter faria a
 * chegada na entrega aparecer como etapa 1, que é o contrário do que aconteceu.
 *
 * Os fretes continuam agrupados. Ordenar a tabela inteira por hora misturaria motoristas diferentes
 * na mesma sequência, e não é isso que o relatório de tempo de espera responde.
 */
export function etapasDaLinhaDoTempo(linhas) {
  return (linhas || []).flatMap((linha) =>
    (linha.linhaDoTempo || [])
      .map((etapa, indice) => ({
        chave: `${linha.id}-${indice}`,
        frete: linha.numberSerial ?? linha.id,
        ordem: indice + 1,
        ponto: etapa.typeOperation === "UNLOADING" ? "Entrega" : "Coleta",
        status: rotuloDoStatus(etapa.status),
        inicio: dataHora(etapa.inicioMs),
        fim: etapa.aberta ? "em andamento" : dataHora(etapa.fimMs),
        minutos: etapa.minutos,
        // Vazio na troca do operador e em tudo anterior ao app novo, que não gravavam posição.
        localizacao: etapa.cidade ?? "",
        observacao: etapa.suspeita
          ? "fora do cálculo: faltou evento depois desta etapa"
          : "",
      }))
      .reverse(),
  );
}
